import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import type { BouncedEmailEntry, ResendResult } from "@/interfaces/bouncedEmails";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { isValidNewEmail, newEmailError } from "@/utils/reportsBounces-helper";

const SENT_LINGER_MS = 1500;
const FADE_MS = 400;

const RESEND_FAILED = "The gift could not be resent.";
const CAMPAIGN_NOT_LIVE = "Campaign ended";

const checkboxSx = {
  p: 0,
  color: "var(--color-smoke)",
  "&.Mui-checked": { color: "var(--color-good-sage)" },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 16,
    borderRadius: "var(--radius-btn)",
    height: 52,
    "& fieldset": { borderColor: "var(--color-smoke)" },
    "&:hover fieldset": { borderColor: "var(--color-graphite)" },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "var(--color-graphite)",
    opacity: 1,
  },
};

const resendButtonSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "2px",
  borderRadius: "var(--radius-btn)",
  textTransform: "uppercase",
  height: 44,
  px: 2.5,
  bgcolor: "var(--color-wax)",
  color: "var(--color-graphite)",
  "&:hover": { bgcolor: "var(--color-smoke)" },
  "&.Mui-disabled": {
    bgcolor: "var(--color-wax)",
    color: "var(--color-graphite)",
    opacity: 0.5,
  },
} as const;

export interface BouncedEmailsModalProps {
  open: boolean;
  onClose: () => void;
  bouncedEmails: BouncedEmailEntry[];
  loading?: boolean;
  onResend: (entries: { contactId: string; newEmail: string }[]) => Promise<ResendResult>;
  /** False once the campaign is no longer live: the server refuses every resend, so none is offered. */
  canResend: boolean;
}

export default function BouncedEmailsModal({
  open,
  onClose,
  bouncedEmails,
  loading = false,
  onResend,
  canResend,
}: BouncedEmailsModalProps) {
  const { can } = usePermissions();
  const canSend = can("send", "CAMPAIGN") && canResend;
  const [newEmails, setNewEmails] = useState<Record<string, string>>({});
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [fadingIds, setFadingIds] = useState<Set<string>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState<{ sent: number; failedNames: string[] } | null>(null);

  const scheduleRemoval = (ids: string[]) => {
    setTimeout(() => {
      setFadingIds((prev) => new Set([...prev, ...ids]));
      setTimeout(() => {
        setRemovedIds((prev) => new Set([...prev, ...ids]));
      }, FADE_MS);
    }, SENT_LINGER_MS);
  };

  const handleClose = () => {
    setNewEmails({});
    setSentIds(new Set());
    setSelectedIds(new Set());
    setFadingIds(new Set());
    setRemovedIds(new Set());
    setRowErrors({});
    setSummary(null);
    onClose();
  };

  /** Marks only the rows the server confirmed as sent; the rest keep their address and show why. */
  const resend = async (ids: string[]) => {
    if (sending) return;
    setSending(true);
    let errors: Record<string, string>;
    try {
      const result = await onResend(
        ids.map((id) => ({ contactId: id, newEmail: newEmails[id] ?? "" })),
      );
      errors = Object.fromEntries((result.errors ?? []).map((e) => [String(e.contactId), e.error]));
    } catch (error) {
      const message = apiErrorMessage(error, RESEND_FAILED);
      const refused = (error as { response?: { data?: Partial<ResendResult> } }).response?.data;
      const perEntry = new Map((refused?.errors ?? []).map((e) => [String(e.contactId), e.error]));
      errors = Object.fromEntries(ids.map((id) => [id, perEntry.get(id) ?? message]));
    } finally {
      setSending(false);
    }

    const sent = ids.filter((id) => !(id in errors));
    const failedNames = ids
      .filter((id) => id in errors)
      .map((id) => {
        const entry = bouncedEmails.find((e) => e.id === id);
        return entry?.name || entry?.bouncedEmail || id;
      });
    setSummary({ sent: sent.length, failedNames });
    setRowErrors((prev) => ({ ...prev, ...errors }));
    setSentIds((prev) => new Set([...prev, ...sent]));
    setSelectedIds((prev) => new Set([...prev].filter((id) => !sent.includes(id))));
    if (sent.length) scheduleRemoval(sent);
  };

  const handleResend = (id: string) => resend([id]);

  const handleResendSelected = () => resend([...selectedIds]);

  const pendingRows = bouncedEmails.filter((e) => !sentIds.has(e.id));
  const allPendingSelected =
    pendingRows.length > 0 && pendingRows.every((e) => selectedIds.has(e.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(pendingRows.map((e) => e.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleToggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const COLS = "32px 1fr 1.3fr 1.3fr 90px";

  const COL_HEADER_SX = {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--color-dark-dust)",
    textTransform: "uppercase",
    letterSpacing: "2px",
  } as const;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "var(--radius-card)", boxShadow: "0px 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      {/* Title row — no divider below, per design */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 2.5,
          pb: 2,
          px: 3,
        }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "var(--color-rodeo-black)" }}
        >
          Fix {pendingRows.length} Bounced Email{pendingRows.length !== 1 ? "s" : ""}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            border: "1px solid var(--color-smoke)",
            borderRadius: "var(--radius-btn)",
            bgcolor: "background.paper",
            color: "var(--color-graphite)",
            width: 44,
            height: 44,
            "&:hover": { bgcolor: "var(--color-wax)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflowX: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress
              aria-label="Loading"
              size={28}
              sx={{ color: "var(--color-good-wine)" }}
            />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "32px 1fr", md: COLS },
                px: 3,
                py: { xs: 1.5, md: 1.25 },
                gap: 1.5,
                alignItems: "center",
                bgcolor: { xs: "var(--color-wax)", md: "transparent" },
                borderTop: { xs: "1px solid var(--color-smoke)", md: "none" },
                borderBottom: "1px solid var(--color-smoke)",
              }}
            >
              <Checkbox
                size="small"
                checked={allPendingSelected}
                indeterminate={selectedIds.size > 0 && !allPendingSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                disabled={pendingRows.length === 0 || !canSend}
                sx={{
                  p: 0,
                  color: "var(--color-smoke)",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "var(--color-good-sage)" },
                }}
              />
              <Typography sx={COL_HEADER_SX}>
                <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                  Select All
                </Box>
                <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                  Name
                </Box>
              </Typography>
              <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
                Bounced Email
              </Typography>
              <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
                New Email
              </Typography>
              <Typography sx={{ ...COL_HEADER_SX, display: { xs: "none", md: "block" } }}>
                Action
              </Typography>
            </Box>

            {/* Data rows */}
            {bouncedEmails.map((entry) => {
              if (removedIds.has(entry.id)) return null;

              const isSent = sentIds.has(entry.id);
              const isFading = fadingIds.has(entry.id);
              const isSelected = selectedIds.has(entry.id);
              const newEmail = newEmails[entry.id] ?? "";
              const validationError = newEmailError(newEmail, entry.bouncedEmail || null);
              const emailValid = isValidNewEmail(newEmail, entry.bouncedEmail || null);
              const emailError = validationError !== null;
              const rowMessage = validationError ?? rowErrors[entry.id];

              const fadeTransition = {
                transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
                opacity: isFading ? 0 : 1,
                transform: isFading ? "translateX(48px)" : "translateX(0)",
              };

              return (
                <Box
                  key={entry.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "32px 1fr", md: COLS },
                    px: 3,
                    py: { xs: 3, md: 2 },
                    gap: { xs: "12px 12px", md: 1.5 },
                    alignItems: { xs: "start", md: "center" },
                    borderBottom: "1px solid var(--color-smoke)",
                    ...fadeTransition,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    disabled={isSent || !canSend}
                    onChange={(e) => handleToggleSelect(entry.id, e.target.checked)}
                    sx={{ ...checkboxSx, alignSelf: "center" }}
                  />
                  <Typography
                    sx={{ fontSize: 16, color: "var(--color-rodeo-black)", alignSelf: "center" }}
                  >
                    {entry.name}
                  </Typography>

                  {/* Bounced email — spans full width on mobile */}
                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    <Typography
                      sx={{ ...COL_HEADER_SX, display: { xs: "block", md: "none" }, mb: 1 }}
                    >
                      Bounced Email
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: "var(--color-rodeo-black)",
                        wordBreak: "break-word",
                      }}
                    >
                      {entry.bouncedEmail}
                    </Typography>
                  </Box>

                  {/* New email — spans full width on mobile, empty grid cell when sent on desktop */}
                  {isSent ? (
                    <Box sx={{ display: { xs: "none", md: "block" } }} />
                  ) : (
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                      <Typography
                        sx={{ ...COL_HEADER_SX, display: { xs: "block", md: "none" }, mb: 1 }}
                      >
                        New Email
                      </Typography>
                      <TextField
                        placeholder="New Email"
                        value={newEmail}
                        error={emailError}
                        fullWidth
                        disabled={!canSend}
                        onChange={(e) => {
                          setNewEmails((prev) => ({ ...prev, [entry.id]: e.target.value }));
                          setRowErrors(({ [entry.id]: _, ...rest }) => rest);
                        }}
                        sx={textFieldSx}
                      />
                      {rowMessage && (
                        <Typography
                          role="alert"
                          sx={{ mt: 0.5, fontSize: 12, color: "var(--color-good-wine)" }}
                        >
                          {rowMessage}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Action — spans full width on mobile */}
                  <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
                    {isSent ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 14, color: "var(--color-good-sage)" }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--color-good-sage)",
                            textTransform: "uppercase",
                            letterSpacing: "1.98px",
                          }}
                        >
                          Sent
                        </Typography>
                      </Box>
                    ) : !canResend ? (
                      <Typography
                        sx={{ fontSize: 13, fontStyle: "italic", color: "var(--color-dark-dust)" }}
                      >
                        {CAMPAIGN_NOT_LIVE}
                      </Typography>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => handleResend(entry.id)}
                        disabled={!emailValid || !canSend || sending}
                        disableElevation
                        sx={resendButtonSx}
                      >
                        Resend
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}

            {summary && (
              <div role="status" className="flex flex-col gap-2 px-6 pt-4">
                {summary.sent > 0 && (
                  <span className="inline-flex items-center gap-1 self-start rounded-full bg-wax px-3 py-1 text-[13px] font-bold text-good-sage">
                    <CheckCircleOutlineIcon fontSize="small" />
                    Resent {summary.sent} gift{summary.sent !== 1 ? "s" : ""}
                  </span>
                )}
                {summary.failedNames.length > 0 && (
                  <p className="text-[13px] text-good-wine">
                    Could not resend: {summary.failedNames.join(", ")}
                  </p>
                )}
              </div>
            )}

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
              }}
            >
              <Button
                onClick={handleClose}
                disableRipple
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "1.98px",
                  color: "var(--color-dark-dust)",
                  textTransform: "uppercase",
                  p: 0,
                  minWidth: 0,
                  "&:hover": { bgcolor: "transparent", color: "var(--color-rodeo-black)" },
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleResendSelected}
                disabled={
                  selectedIds.size === 0 ||
                  bouncedEmails.some(
                    (row) =>
                      selectedIds.has(row.id) &&
                      !isValidNewEmail(newEmails[row.id] ?? "", row.bouncedEmail || null),
                  ) ||
                  !canSend ||
                  sending
                }
                disableElevation
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "1.98px",
                  borderRadius: "var(--radius-btn)",
                  textTransform: "uppercase",
                  height: 44,
                  bgcolor: "var(--color-good-wine)",
                  color: "white",
                  "&:hover": { bgcolor: "var(--color-good-wine-2)" },
                  "&.Mui-disabled": {
                    bgcolor: "var(--color-smoke)",
                    color: "var(--color-graphite)",
                  },
                }}
              >
                Resend Selected ({selectedIds.size})
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
